/**
 * Reason codes in support's words (round 2, support R2-M1): what happened, in a phrase that reads
 * without the document's own details, and whose it is to fix, from the reason catalogue's owner
 * (@paige/format `reasons`). Pure.
 */
import { isReasonCode, reasons } from '@paige/format';

const PHRASE: Record<string, string> = {
  source_file_unreadable: 'files that could not be opened',
  unsupported_format: 'files in a format Paige does not read',
  page_count_mismatch: 'files whose page count did not match',
  render_failed: 'pages that could not be rendered',
  ocr_failed: 'pages that could not be read',
  reader_refused: 'pages Cortexa could not make sense of',
  reader_unavailable: 'reading waits for Cortexa to catch up',
  pool_quota_exhausted: 'Cortexa at capacity',
  lease_expired_repeatedly: 'documents that keep timing out',
  ambiguous_unresolved: 'a split to confirm: two documents may be one',
  destination_auth_failed: 'the destination refuses the saved sign-in',
  destination_mapping_invalid: 'the destination rejects a field',
  destination_unreachable: 'the destination is not answering',
  destination_ambiguous: 'more than one destination matches',
  tenant_over_quota: 'over the page quota',
  credits_exhausted: 'out of pages',
  tenant_paused: 'intake paused',
  sender_quarantined: 'email held from an unknown sender',
  engine_stalled: 'a job stopped moving; Paige is restarting it',
  engine_unrecoverable: 'a job that could not be restarted',
  engine_workspace_mismatch: 'a job workspace that does not match its record',
  workspace_migration_failed: 'a job being updated',
  workspace_restore_failed: 'a job being restored',
  disk_attach_failed: 'job storage being reattached',
  disk_full: 'job storage full',
  projection_lagging: 'the overview catching up',
  reader_generation_retired: 'documents to reread with the current Cortexa',
  learning_gate_refused: 'a learning gate still watching',
  answer_truncated: 'answers cut short; Cortexa asks again',
  stage_timeout: 'a step that took too long',
  source_file_set_aside: 'files set aside: they could not be opened safely',
};

/** "a split to confirm: two documents may be one"; an unknown code reads as its words. */
export function reasonPhrase(code: string | null): string {
  if (!code) return 'no reason recorded';
  return PHRASE[code] ?? code.replace(/_/g, ' ');
}

/** "theirs to fix" when the catalogue says the customer clears it; "ours to fix" otherwise. */
export function reasonOwnerWord(code: string | null): string | null {
  if (!code || !isReasonCode(code)) return null;
  return reasons[code].owner === 'customer' ? 'theirs to fix' : 'ours to fix';
}

/** The phrase with its owner: "the destination is not answering (theirs to fix)". */
export function reasonWords(code: string | null): string {
  const owner = reasonOwnerWord(code);
  return owner ? `${reasonPhrase(code)} (${owner})` : reasonPhrase(code);
}
